import type { MarketType, Runners } from '@ps/isp-web-services-types';
import type { TranslationTag } from '@ps/web-core-intl';
import { useEffect, useState } from 'react';

import type { Event, Header } from '@swf/sports/components/Event/types';
import { EventTypeId } from '@swf/sports/types/eventType';
import buildMarketHeadersFor from '@swf/sports/utils/marketHeadersMapper';

import {
  TXTCLI_No,
  TXTCLI_Score,
  TXTCLI_Yes,
  TXTWCL_Away,
  TXTWCL_Draw,
  TXTWCL_Home_8,
  TXTWCL_Line,
  TXTWCL_No_Goal,
  TXTWCL_Over,
  TXTWCL_Under
} from './useMarketHeadersFromEvents.messages.json';

const runnerResultTypeTranslation: Partial<Record<string, TranslationTag>> = {
  HOME: TXTWCL_Home_8,
  AWAY: TXTWCL_Away,
  DRAW: TXTWCL_Draw,
  OVER: TXTWCL_Over,
  UNDER: TXTWCL_Under,
  LINE: TXTWCL_Line,
  SCORE: TXTCLI_Score,
  NO_GOAL: TXTWCL_No_Goal,
  YES: TXTCLI_Yes,
  NO: TXTCLI_No
};

const shouldUseMapperForMarket = (eventTypeId: EventTypeId, marketType: MarketType) => {
  const exceptions: Partial<Record<EventTypeId, MarketType>> = {
    [EventTypeId.TENNIS]: 'MATCH_BETTING',
    [EventTypeId.DARTS]: 'MATCH_BETTING',
    [EventTypeId.SNOOKER]: 'MATCH_BETTING',
    [EventTypeId.BOXING]: 'BOXING_MATCH_BETTING',
    [EventTypeId.MIXED_MARTIAL_ARTS]: 'MATCH_BETTING'
  };

  return exceptions[eventTypeId] === marketType;
};

export function getMarketRunners(
  marketType: MarketType,
  runners: Runners,
  eventTypeId: EventTypeId
): (TranslationTag | string)[] {
  if (!runners[0].resultType || shouldUseMapperForMarket(eventTypeId, marketType)) {
    return buildMarketHeadersFor({ marketType, runners, eventTypeId }).map(
      (headerString) => runnerResultTypeTranslation[headerString] ?? headerString
    );
  }

  return runners.map((runner) =>
    runner.resultType ? runnerResultTypeTranslation[runner.resultType] ?? '' : ''
  );
}

function useMarketHeadersFromEvents(events: Event[]) {
  const [header, setHeader] = useState<Header>({ name: '', runners: [] });
  const { market, eventTypeId } = events.find((e) => e.market && e.market.marketType) ?? {};

  useEffect(() => {
    if (market && market.marketType && eventTypeId) {
      setHeader({
        name: market.marketName,
        runners: getMarketRunners(market.marketType, market.runners, eventTypeId)
      });
    }
  }, [market, market?.marketType, eventTypeId]);

  return header;
}

export default useMarketHeadersFromEvents;
